// MarkdownBlock — single global wrapper for all markdown rendering in v2.
//
// Composes:
//   - react-markdown (core)
//   - remark-math + rehype-katex (LaTeX inline + block)
//   - mdComponents from md-with-mermaid (```mermaid → SVG)
//
// Decision (Session 19R · Q1 KaTeX integration):
//   Single component instead of per-view ReactMarkdown calls. Adding new
//   markdown-level features (math, mermaid, charts ...) becomes a one-file
//   change. All views in v2 import this and never touch ReactMarkdown directly.
//
// Session 19AQ · F3 (rendering defenses):
//   - empty / whitespace-only children → return null (no empty <p>)
//   - KaTeX `throwOnError: false` + `strict: 'ignore'` → malformed LaTeX
//     renders as red inline text instead of crashing the page (this is the
//     "数理题生成的格式有小问题" scenario the user flagged for early proof_drill
//     output).
//   - ErrorBoundary wraps the markdown render so any other render-time error
//     degrades to a plain <pre> dump instead of unmounting the whole view.
//
// API:
//   <MarkdownBlock>{text}</MarkdownBlock>          // block context (default)
//   <MarkdownBlock inline>{text}</MarkdownBlock>   // strip outer <p>

import { Component, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import { mdComponents } from './md-with-mermaid.js';
import { recordEvent } from '../storage/idb.js';
import 'katex/dist/katex.min.css';

const inlineComponents: Components = {
  ...mdComponents,
  p: ({ children }) => <>{children}</>,
};

/** rehype-katex options — fail soft on bad input. */
const katexOptions = {
  // KaTeX raises a ParseError on bad LaTeX by default; flip to render a
  // red \textrm block instead so the rest of the markdown still renders.
  throwOnError: false,
  // 'ignore' = silently allow non-strict KaTeX features (e.g. \begin{align*}).
  strict: 'ignore' as const,
  errorColor: '#b91c1c',
};

export interface MarkdownBlockProps {
  children: string;
  inline?: boolean;
}

class MarkdownErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }
  override componentDidCatch(error: unknown): void {
    // eslint-disable-next-line no-console
    console.warn('[MarkdownBlock] render error:', error);
    // Session 19AS · G9: persist render error so the operator dashboard sees
    // it. Best-effort — recordEvent itself swallows write failures.
    const msg = error instanceof Error ? error.message : String(error);
    void recordEvent({
      type: 'render-error',
      summary: `MarkdownBlock: ${msg.slice(0, 180)}`,
      viewPath: typeof window !== 'undefined' ? window.location.pathname : undefined,
      payload: {
        component: 'MarkdownBlock',
        stack: error instanceof Error ? error.stack?.slice(0, 4000) : undefined,
      },
    });
  }
  override render(): ReactNode {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

export function MarkdownBlock({ children, inline = false }: MarkdownBlockProps) {
  // 19AQ · F3: empty / whitespace-only → render nothing (avoid empty <p>
  // that creates ghost vertical rhythm in lists / cards).
  if (typeof children !== 'string' || children.trim().length === 0) return null;

  const fallback = (
    <pre style={{ whiteSpace: 'pre-wrap', color: 'var(--color-error)', fontSize: '0.85rem' }}>
      {children}
    </pre>
  );

  return (
    <MarkdownErrorBoundary fallback={fallback}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[[rehypeKatex, katexOptions]]}
        components={inline ? inlineComponents : mdComponents}
      >
        {children}
      </ReactMarkdown>
    </MarkdownErrorBoundary>
  );
}
